import React, { useState, useEffect } from 'react';
import TransactionList from '../components/financial/TransactionList';
import TransactionForm from '../components/financial/TransactionForm';
import CashFlowReport from '../components/financial/CashFlowReport';
import EmptyState from '../components/EmptyState';
import { db } from '../lib/supabase';
import type { Transaction } from '../types/financial';

const Financial: React.FC = () => {
  const [view, setView] = useState<'receivable' | 'payable' | 'cashflow'>('receivable');
  const [isNewTransaction, setIsNewTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, [view]);

  const loadTransactions = async () => {
    try {
      setIsLoading(true);
      const type = view === 'receivable' ? 'income' : 'expense';
      const data = await db.transactions.list(type);
      setTransactions(data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransactionSubmit = async (data: Transaction) => {
    try {
      await db.transactions.create(data);
      await loadTransactions();
      setIsNewTransaction(false);
    } catch (error) {
      console.error('Error saving transaction:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financeiro</h1>
            <div className="mt-2 flex space-x-4">
              <button
                onClick={() => setView('receivable')}
                className={`px-4 py-2 rounded-md ${
                  view === 'receivable'
                    ? 'bg-blue-100 text-blue-800'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Contas a Receber
              </button>
              <button
                onClick={() => setView('payable')}
                className={`px-4 py-2 rounded-md ${
                  view === 'payable'
                    ? 'bg-blue-100 text-blue-800'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Contas a Pagar
              </button>
              <button
                onClick={() => setView('cashflow')}
                className={`px-4 py-2 rounded-md ${
                  view === 'cashflow'
                    ? 'bg-blue-100 text-blue-800'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Fluxo de Caixa
              </button>
            </div>
          </div>
          
          {view !== 'cashflow' && (
            <button
              onClick={() => setIsNewTransaction(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {view === 'receivable' ? 'Novo Recebimento' : 'Nova Despesa'}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Carregando...</p>
          </div>
        ) : isNewTransaction ? (
          <TransactionForm
            type={view === 'receivable' ? 'income' : 'expense'}
            onSubmit={handleTransactionSubmit}
            onCancel={() => setIsNewTransaction(false)}
          />
        ) : view === 'cashflow' ? (
          <CashFlowReport transactions={transactions} />
        ) : transactions.length === 0 ? (
          <EmptyState
            title={`Nenhum ${view === 'receivable' ? 'recebimento' : 'pagamento'} registrado`}
            description={`Comece registrando ${view === 'receivable' ? 'seus recebimentos' : 'suas despesas'} para controlar seu fluxo de caixa.`}
            action={
              <button
                onClick={() => setIsNewTransaction(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {view === 'receivable' ? 'Registrar Recebimento' : 'Registrar Despesa'}
              </button>
            }
          />
        ) : (
          <TransactionList
            transactions={transactions}
            onSelect={setSelectedTransaction}
          />
        )}
      </div>
    </div>
  );
};

export default Financial;